import React, { useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
    DEFAULT_INDICATOR_COLOR,
    DEFAULT_INDICATOR_FONT_SIZE_XS,
    UNIT_TO_METER_DIVISOR
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface WindowLengthIndicatorProps {
    windowWidth: number;
    rotation: number; 
    color?: string;
}

const WindowLengthIndicator = ({
    windowWidth,
    rotation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    color = DEFAULT_INDICATOR_COLOR,
}: WindowLengthIndicatorProps): React.JSX.Element => {
    const {locale} = useLanguage();
    const {unitType} = useUnitContext();
    const intl = useIntl();
    const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "meter"), [intl]);
    const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "foot"), [intl]);
    
    const sideAlengthText = useMemo(() => unitType === EUnitType.METRIC ? localizeNumber((windowWidth/UNIT_TO_METER_DIVISOR).toFixed(2), locale) + mLocalizedLabel : localizeNumber(convertTo(windowWidth/UNIT_TO_METER_DIVISOR, "meters" as UnitsByMeasure<MeasureKind.Length>, "feet" as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) + ftLocalizedLabel, [locale, unitType, windowWidth]);

    return (
        <group
            name={"group-windowLengthIndicator"}
            position={[0, 0, 1]}
            renderOrder={RENDER_ORDERS.lengthIndicator}
        >
            {/* Text group with negated rotation to counteract parent rotation */}
            <group rotation={[0, 0, -rotation]}  position={[0, 0, 1]}>
                <RoundedBoxWithText
                    boxSize={[3,1,0]}
                    position={new THREE.Vector3(0, 0, 0)}
                    fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
                    textColor="black"
                    text={sideAlengthText}
                />
            </group>
        </group>
    );
};

export default WindowLengthIndicator;